import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { ActionModal } from "../ActionModal/ActionModal.js";
import { Button, type ButtonProps } from "../Button/Button.js";
import { registerUnsavedEdits } from "./unsavedEdits.js";

/**
 * ONE way out of a dialog. The sheet's grabber, the scrim, Escape, the modal's ✕ and every Cancel button all call
 * the same `requestClose` (PO, 2026-10-04): with nothing changed it closes; with edits in the dialog it first asks
 * "Discard changes?". Before this each exit was its own path, so a Cancel could silently throw a draft away while a
 * stray pull on the grabber did the same by another route, and the grabber itself had to stay inert because nothing
 * stood between it and a half-typed form.
 *
 * SAVING never goes through here — a save closes with the caller's own `onClose`, because nothing is being
 * discarded. Only DISMISSING does.
 */

/** Whether the dialog holds unsaved edits: an explicit boolean, or `"auto"` to infer it from the user typing. */
export type DialogDirty = boolean | "auto";

const RequestCloseContext = createContext<(() => boolean) | null>(null);

/**
 * The enclosing dialog's close request, for a control rendered INSIDE it (a footer's Cancel). Returns `true` when the
 * dialog closed, `false` when it is asking to discard first. Outside a guarded dialog there is nothing to request.
 */
export function useRequestClose(): (() => boolean) | null {
  return useContext(RequestCloseContext);
}

/**
 * A Cancel button that leaves through the dialog's one exit — so it asks before discarding edits exactly as the
 * grabber, scrim and Escape do. Use it in place of `<Button onClick={onClose}>Cancel</Button>` in any Modal, Sheet or
 * FormDialog. Defaults to the ghost variant and the word "Cancel"; every Button prop passes through.
 */
export function DialogCancel({ children = "Cancel", variant = "ghost", onClick, ...rest }: ButtonProps) {
  const requestClose = useRequestClose();
  // Outside a Modal / Sheet / FormDialog there is no exit to request, and the button would do nothing — say so
  // rather than ship a dead Cancel silently (a test rendering one bare will surface this too).
  if (!requestClose) {
    console.error("DialogCancel rendered outside a Modal, Sheet or FormDialog — it has no dialog to close.");
  }
  return (
    <Button
      type="button"
      variant={variant}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) requestClose?.();
      }}
    >
      {children}
    </Button>
  );
}

/**
 * The guard a dialog primitive folds in. `"auto"` watches the dialog's own DOM: any `input`/`change` from a field
 * inside it marks the dialog edited, unless the field (or an ancestor) carries `data-no-dirty` — a search box that
 * filters a list is not a draft. The flag lives in a ref, so typing re-renders nothing.
 */
export function useCloseGuard({
  onClose,
  dirty,
  root,
}: {
  onClose: () => void;
  dirty: DialogDirty | undefined;
  root: RefObject<HTMLElement | null>;
}): { requestClose: () => boolean; provide: (children: ReactNode) => ReactNode; confirm: ReactNode } {
  const [asking, setAsking] = useState(false);
  const edited = useRef(false);

  useEffect(() => {
    const el = root.current;
    if (dirty !== "auto" || !el) return;
    const mark = (e: Event) => {
      const target = e.target as Element | null;
      if (target?.closest?.("[data-no-dirty]")) return;
      edited.current = true;
    };
    el.addEventListener("input", mark, true);
    el.addEventListener("change", mark, true);
    return () => {
      el.removeEventListener("input", mark, true);
      el.removeEventListener("change", mark, true);
    };
  }, [dirty, root]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // App-wide, so the live updater can tell a draft is open (unsavedEdits.ts).
  useEffect(
    () =>
      registerUnsavedEdits(() => {
        const d = dirtyRef.current;
        return d === "auto" ? edited.current : d === true;
      }),
    [],
  );

  const requestClose = useCallback(() => {
    const d = dirtyRef.current;
    const hasEdits = d === "auto" ? edited.current : d === true;
    if (!hasEdits) {
      onCloseRef.current();
      return true;
    }
    setAsking(true);
    return false;
  }, []);

  const provide = (children: ReactNode) => (
    <RequestCloseContext.Provider value={requestClose}>{children}</RequestCloseContext.Provider>
  );

  // Portalled: the dialog it guards may be mid-drag (a transformed sheet panel is a containing block for anything
  // fixed inside it) or mid-entrance, and the confirm must sit on the viewport above both.
  const confirm =
    asking && typeof document !== "undefined"
      ? createPortal(
          <ActionModal
            title="Discard changes?"
            confirmLabel="Discard"
            cancelLabel="Keep editing"
            confirmTone="danger"
            onCancel={() => setAsking(false)}
            onConfirm={() => {
              setAsking(false);
              edited.current = false;
              onCloseRef.current();
            }}
          >
            What you've entered here hasn't been saved.
          </ActionModal>,
          document.body,
        )
      : null;

  return { requestClose, provide, confirm };
}
